import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import {
  getAiVisibilityByMonth,
  listPromptObservations,
  logAiObservation,
  type PromptObservation,
} from "@/lib/ai-citations.functions";
import {
  AI_PROMPTS,
  AI_PROMPT_CLUSTER_LABELS,
  AI_SURFACES,
  type AiPromptCluster,
} from "@/content/ai-prompts";

export const Route = createFileRoute("/_authenticated/admin/ai-synlighet")({
  head: () => ({
    meta: [
      { title: "AI-synlighet — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AiVisibilityPage,
});

const CLUSTERS: AiPromptCluster[] = ["film", "foto", "heritage", "ort"];

function AiVisibilityPage() {
  const [surface, setSurface] = useState<string>(AI_SURFACES[0].id);
  const [openPrompt, setOpenPrompt] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [formulation, setFormulation] = useState("");
  const [isCited, setIsCited] = useState(true);
  const [isLinked, setIsLinked] = useState(false);
  const [coreOnly, setCoreOnly] = useState(true);

  const listFn = useServerFn(listPromptObservations);
  const monthsFn = useServerFn(getAiVisibilityByMonth);
  const logFn = useServerFn(logAiObservation);
  const qc = useQueryClient();

  const obs = useQuery({
    queryKey: ["admin", "ai-observations"],
    queryFn: () => listFn(),
  });

  const months = useQuery({
    queryKey: ["admin", "ai-visibility-months"],
    queryFn: () => monthsFn(),
  });

  const save = useMutation({
    mutationFn: (input: {
      query: string;
      mentioned?: boolean;
      cited: boolean;
      linked?: boolean;
      formulation?: string;
      cited_url?: string;
      note?: string;
    }) => logFn({ data: { source: surface, ...input } }),
    onSuccess: () => {
      setOpenPrompt(null);
      setUrl("");
      setNote("");
      setFormulation("");
      setIsCited(true);
      setIsLinked(false);
      qc.invalidateQueries({ queryKey: ["admin", "ai-observations"] });
      qc.invalidateQueries({ queryKey: ["admin", "ai-visibility-months"] });
    },
  });

  const bySurface = useMemo(() => {
    const map = new Map<string, PromptObservation>();
    for (const o of obs.data ?? []) {
      if (o.source !== surface) continue;
      if (!map.has(o.query)) map.set(o.query, o); // senaste först i listan
    }
    return map;
  }, [obs.data, surface]);

  const promptList = useMemo(
    () => AI_PROMPTS.filter((p) => (coreOnly ? p.core : true)),
    [coreOnly],
  );
  const done = [...bySurface.keys()].filter((q) =>
    promptList.some((p) => p.prompt === q),
  ).length;
  const hits = [...bySurface.values()].filter((o) => o.cited).length;
  const mentions = [...bySurface.values()].filter((o) => o.mentioned).length;

  return (
    <div className="p-5 md:p-8 space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">AI-synlighet</h1>
        <p className="text-sm text-white/50 max-w-2xl">
          Kör frågorna i respektive AI-tjänst en gång i månaden och notera om
          Fenny nämns, citeras och länkas — samt med vilken formulering. Ändra
          aldrig frågeformuleringarna, då går serien inte att jämföra över tid.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {AI_SURFACES.map((s) => (
          <button
            key={s.id}
            onClick={() => setSurface(s.id)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              surface === s.id
                ? "border-white/30 bg-white/10 text-white"
                : "border-white/10 text-white/60 hover:bg-white/5"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => setCoreOnly(!coreOnly)}
          className={`rounded-full border px-3 py-1.5 text-sm ${
            coreOnly
              ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
              : "border-white/10 text-white/60 hover:bg-white/5"
          }`}
        >
          {coreOnly ? "Visar månadens 30 kärnfrågor" : "Visar alla frågor"}
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 text-sm text-white/70">
        {done} av {promptList.length} frågor besvarade för den här tjänsten ·{" "}
        <span className="text-white/80">{mentions} omnämnanden</span> ·{" "}
        <span className="text-emerald-400">{hits} citeringar</span>
      </div>

      <section className="rounded-xl border border-white/10 bg-white/[0.02]">
        <h2 className="border-b border-white/10 px-5 py-3 text-sm font-semibold">
          GEO-KPI per månad
        </h2>
        {months.isLoading ? (
          <div className="px-5 py-4 text-sm text-white/40">Laddar …</div>
        ) : (months.data ?? []).length === 0 ? (
          <div className="px-5 py-4 text-sm text-white/40">
            Inga observationer loggade ännu.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-white/40">
              <tr>
                <th className="px-5 py-2">Månad</th>
                <th className="px-5 py-2">Obs.</th>
                <th className="px-5 py-2">Omnämnd</th>
                <th className="px-5 py-2">Citerad</th>
                <th className="px-5 py-2">Länkad</th>
                <th className="px-5 py-2">Andel omnämnd</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(months.data ?? []).map((m) => (
                <tr key={m.month}>
                  <td className="px-5 py-2 text-white">{m.month}</td>
                  <td className="px-5 py-2 text-white/70">{m.observations}</td>
                  <td className="px-5 py-2 text-white/70">{m.mentioned}</td>
                  <td className="px-5 py-2 text-emerald-300">{m.cited}</td>
                  <td className="px-5 py-2 text-white/70">{m.linked}</td>
                  <td className="px-5 py-2 text-white/70">{m.mentionRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {CLUSTERS.map((cluster) => {
        const items = promptList.filter((p) => p.cluster === cluster);
        if (items.length === 0) return null;
        return (
          <section key={cluster} className="rounded-xl border border-white/10 bg-white/[0.02]">
            <h2 className="border-b border-white/10 px-5 py-3 text-sm font-semibold">
              {AI_PROMPT_CLUSTER_LABELS[cluster]}{" "}
              <span className="text-white/40">({items.length})</span>
            </h2>
            <ul className="divide-y divide-white/5">
              {items.map((p) => {
                const logged = bySurface.get(p.prompt);
                const isOpen = openPrompt === p.prompt;
                return (
                  <li key={p.id} className="px-5 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-white">
                          <span className="text-white/40 mr-2">{p.id}.</span>
                          {p.prompt}
                        </div>
                        {logged && (
                          <div className="mt-1 text-xs text-white/40">
                            {logged.cited
                              ? "Citerad"
                              : logged.mentioned
                                ? "Omnämnd"
                                : "Ingen träff"}
                            {logged.linked ? " · länkad" : ""} ·{" "}
                            {new Date(logged.detected_at).toLocaleDateString("sv-SE")}
                            {logged.cited_url ? ` · ${logged.cited_url}` : ""}
                            {logged.formulation ? (
                              <div className="mt-0.5 italic text-white/50">
                                “{logged.formulation}”
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          onClick={() => {
                            setOpenPrompt(isOpen ? null : p.prompt);
                            setUrl("");
                            setNote("");
                            setFormulation("");
                            setIsCited(true);
                            setIsLinked(false);
                          }}
                          className="rounded-lg border border-emerald-400/30 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-400/10"
                        >
                          <Check className="mr-1 inline h-3.5 w-3.5" /> Omnämnd
                        </button>
                        <button
                          onClick={() =>
                            save.mutate({ query: p.prompt, cited: false })
                          }
                          disabled={save.isPending}
                          className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/60 hover:bg-white/5 disabled:opacity-50"
                        >
                          <X className="mr-1 inline h-3.5 w-3.5" /> Ingen träff
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="mt-3 space-y-2">
                        <div className="flex flex-wrap gap-4 text-xs text-white/60">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isCited}
                              onChange={(e) => setIsCited(e.target.checked)}
                            />
                            Citerad som källa
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isLinked}
                              onChange={(e) => setIsLinked(e.target.checked)}
                            />
                            Klickbar länk till fenny.se
                          </label>
                        </div>
                        <input
                          value={formulation}
                          onChange={(e) => setFormulation(e.target.value)}
                          placeholder="Ordagrann formulering om Fenny i svaret"
                          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30"
                        />
                        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                        <input
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="Citerad sida, t.ex. https://fenny.se/priser"
                          className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30"
                        />
                        <input
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="Hur beskrevs Fenny? Något fel?"
                          className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30"
                        />
                        <button
                          onClick={() =>
                            save.mutate({
                              query: p.prompt,
                              mentioned: true,
                              cited: isCited,
                              linked: isLinked,
                              formulation: formulation || undefined,
                              cited_url: url || undefined,
                              note: note || undefined,
                            })
                          }
                          disabled={save.isPending}
                          className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
                        >
                          {save.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Spara"
                          )}
                        </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
